import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, RefreshCw } from 'lucide-react';
import {
  flipkartAdsApi,
  flipkartAdsOauthApi,
  type FlipkartAdsAdAccount,
  type FlipkartAdsCampaign,
  type FlipkartAdsOauthBootstrap,
  type FlipkartAdsOauthConnection,
} from '@/api/client';
import {
  allowedFlipkartAdsMessageOrigins,
  extractFlipkartAdsOauthCode,
  FLIPKART_ADS_LOGIN_WRAPPER_ID,
  formatFlipkartAdsSdkError,
  loadFlipkartAdsSdk,
  parseFlipkartAdsOauthMessageEvent,
  flipkartAdsConnectMessage,
} from '@/api/flipkartAdsOauth';
import { ConnectionAccountCard } from '@/components/connections/ConnectionAccountCard';
import { ADVERTISING_ACCOUNT_REGIONS, flipkartAccountRegion } from '@/api/advertisingAccountRegions';
import { flipkartAdAccountLabel } from '@/api/workspaceScope';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrap,
} from '@/components/ui/Table';

export const FLIPKART_ADS_OAUTH_CONNECTIONS_QUERY_KEY = [
  'flipkart-ads-oauth-connections',
] as const;

export const FLIPKART_ADS_ALL_ACCOUNTS_QUERY_KEY = ['flipkart-ads-accounts'] as const;

export function flipkartAdsCampaignsQueryKey(credentialId: string, adAccountId?: string) {
  return adAccountId
    ? (['flipkart-ads-campaigns', credentialId, adAccountId] as const)
    : (['flipkart-ads-campaigns', credentialId] as const);
}

export function flipkartAdsAccountsQueryKey(credentialId: string) {
  return ['flipkart-ads-accounts', credentialId] as const;
}

export function flipkartAdsAdGroupsQueryKey(credentialId: string, adAccountId?: string) {
  return adAccountId
    ? (['flipkart-ads-ad-groups', credentialId, adAccountId] as const)
    : (['flipkart-ads-ad-groups', credentialId] as const);
}

export function flipkartAdsKeywordsQueryKey(credentialId: string, adAccountId?: string) {
  return adAccountId
    ? (['flipkart-ads-keywords', credentialId, adAccountId] as const)
    : (['flipkart-ads-keywords', credentialId] as const);
}

export function flipkartAdsNegativeKeywordsQueryKey(credentialId: string, adAccountId?: string) {
  return adAccountId
    ? (['flipkart-ads-negative-keywords', credentialId, adAccountId] as const)
    : (['flipkart-ads-negative-keywords', credentialId] as const);
}

export function flipkartAdsProductsQueryKey(credentialId: string, adAccountId?: string) {
  return adAccountId
    ? (['flipkart-ads-products', credentialId, adAccountId] as const)
    : (['flipkart-ads-products', credentialId] as const);
}

export function flipkartAdsReportsQueryKey(
  credentialId: string,
  grain?: string,
  adAccountId?: string
) {
  if (grain && adAccountId) {
    return ['flipkart-ads-reports', credentialId, grain, adAccountId] as const;
  }
  if (grain) {
    return ['flipkart-ads-reports', credentialId, grain] as const;
  }
  return ['flipkart-ads-reports', credentialId] as const;
}

function axiosErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && 'message' in detail) {
      return String((detail as { message?: string }).message || error.message);
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
}

function healthLabel(health: string): string {
  switch (health) {
    case 'reauth_required':
      return 'Re-authorization required';
    case 'expiring_soon':
      return 'Expiring soon';
    case 'inactive_risk':
      return 'At risk (inactive)';
    default:
      return 'Active';
  }
}

function needsReconnect(health: string): boolean {
  return (
    health === 'reauth_required' || health === 'expiring_soon' || health === 'inactive_risk'
  );
}

export function useFlipkartAdsOauthConnections(enabled = true) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;

    const refetchConnections = () => {
      void queryClient.invalidateQueries({ queryKey: FLIPKART_ADS_OAUTH_CONNECTIONS_QUERY_KEY });
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refetchConnections();
    };

    window.addEventListener('focus', refetchConnections);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', refetchConnections);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, queryClient]);

  return useQuery({
    queryKey: FLIPKART_ADS_OAUTH_CONNECTIONS_QUERY_KEY,
    queryFn: async () => {
      const res = await flipkartAdsOauthApi.listConnections();
      if (!res.success) throw new Error('Failed to load Flipkart Ads connections');
      return res.data;
    },
    enabled,
    refetchInterval: 5 * 60_000,
  });
}

export function FlipkartAdsConnectionsPanel() {
  const { data, isLoading, isError, refetch } = useFlipkartAdsOauthConnections();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [reconnectCredentialId, setReconnectCredentialId] = useState<string | null>(null);
  const bootstrapRef = useRef<FlipkartAdsOauthBootstrap | null>(null);
  const reconnectIdRef = useRef<string | null>(null);
  const exchangingRef = useRef(false);
  const exchangedCodesRef = useRef(new Set<string>());

  reconnectIdRef.current = reconnectCredentialId;

  const bootstrapQuery = useQuery({
    queryKey: ['flipkart-ads-oauth-bootstrap', reconnectCredentialId],
    queryFn: async () => {
      const res = await flipkartAdsOauthApi.getAuthorize(reconnectCredentialId);
      if (!res.success || !res.data?.client_id) {
        throw new Error('Could not start Flipkart Ads authorization');
      }
      return res.data;
    },
  });

  bootstrapRef.current = bootstrapQuery.data ?? null;

  const exchange = useMutation({
    mutationFn: async (code: string) => {
      if (exchangedCodesRef.current.has(code) || exchangingRef.current) {
        return null;
      }
      exchangingRef.current = true;
      try {
        const bootstrap = bootstrapRef.current;
        const res = await flipkartAdsOauthApi.exchange({
          code,
          state: bootstrap?.state,
          credential_id: reconnectIdRef.current,
        });
        if (!res.success) throw new Error('Could not complete Flipkart Ads authorization');
        exchangedCodesRef.current.add(code);
        return res.data;
      } finally {
        exchangingRef.current = false;
      }
    },
    onSuccess: (result) => {
      if (!result) return;
      showToast(flipkartAdsConnectMessage(Boolean(result.reused)), 'success');
      setReconnectCredentialId(null);
      void queryClient.invalidateQueries({ queryKey: FLIPKART_ADS_OAUTH_CONNECTIONS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FLIPKART_ADS_ALL_ACCOUNTS_QUERY_KEY });
    },
    onError: (err: unknown) => {
      showToast(axiosErrorMessage(err) || 'Flipkart Ads authorization failed', 'error');
    },
  });

  const exchangeCode = useRef(exchange.mutate);
  exchangeCode.current = exchange.mutate;

  useEffect(() => {
    const bootstrap = bootstrapQuery.data;
    if (!bootstrap) return;
    let cancelled = false;

    void (async () => {
      try {
        await loadFlipkartAdsSdk(bootstrap.sdk_script_url);
        if (cancelled) return;
        if (!window.FKOAuthClient) {
          throw new Error('Flipkart Ads SDK did not initialize');
        }
        const wrapper = document.getElementById(FLIPKART_ADS_LOGIN_WRAPPER_ID);
        if (wrapper) wrapper.replaceChildren();
        new window.FKOAuthClient({
          clientId: bootstrap.client_id,
          redirectUri: bootstrap.redirect_uri,
          element: FLIPKART_ADS_LOGIN_WRAPPER_ID,
          onSuccess: (payload) => {
            const code = extractFlipkartAdsOauthCode(payload);
            if (!code) {
              showToast('Flipkart Ads authorization did not return a code', 'error');
              return;
            }
            exchangeCode.current(code);
          },
          onError: (error) => {
            showToast(formatFlipkartAdsSdkError(error), 'error');
          },
        }).init();
      } catch (err) {
        if (!cancelled) {
          showToast(axiosErrorMessage(err) || 'Could not load Flipkart Ads login', 'error');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bootstrapQuery.data, showToast]);

  useEffect(() => {
    const allowedOrigins = allowedFlipkartAdsMessageOrigins(
      window.location.origin,
      import.meta.env.VITE_API_BASE_URL as string | undefined
    );

    const onMessage = (event: MessageEvent) => {
      const message = parseFlipkartAdsOauthMessageEvent(event, allowedOrigins);
      if (!message) return;
      if (message.error) {
        showToast(message.error, 'error');
        return;
      }
      if (message.code) exchangeCode.current(message.code);
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [showToast]);

  const connections = data?.connections ?? [];
  const showEmpty = !isLoading && !isError && connections.length === 0;
  const loginReady = !bootstrapQuery.isLoading && !bootstrapQuery.isError;

  return (
    <section
      className="overflow-hidden rounded-2xl border border-[#d9e4f5]/90 bg-white shadow-sm"
      aria-labelledby="flipkart-ads-connections-heading"
    >
      <div className="flex flex-wrap items-center justify-between gap-4 bg-[#f4f8fd] px-4 py-4 sm:px-6 sm:py-5">
        <div className="flex min-w-0 items-start gap-3">
          <img
            src="/flipkart.svg"
            alt=""
            className="h-11 w-11 shrink-0 rounded-xl border border-black/5 bg-white p-1.5 shadow-sm"
          />
          <div className="min-w-0">
            <h2 id="flipkart-ads-connections-heading" className="text-base font-semibold text-brand-ink">
              Flipkart Ads
            </h2>
            <p className="mt-0.5 max-w-md text-sm leading-snug text-foreground-muted">
              Connect your Flipkart advertising accounts via the Login SDK. Access tokens last about
              1 day; refresh tokens about 6 months.
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {bootstrapQuery.isLoading ? (
            <div className="flex h-10 items-center text-sm text-foreground-muted">
              <Spinner className="mr-1.5 h-3.5 w-3.5" />
              Loading Flipkart login…
            </div>
          ) : null}
          {bootstrapQuery.isError ? (
            <Button type="button" onClick={() => void bootstrapQuery.refetch()}>
              Retry Flipkart login
            </Button>
          ) : null}
          <div className={loginReady ? 'relative h-10 min-w-[10.75rem]' : undefined}>
            {loginReady ? (
              <div className="pointer-events-none absolute inset-0 z-0 inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-ink px-4 text-sm font-medium text-white shadow-sm">
                <Link2 className="h-3.5 w-3.5" aria-hidden />
                Connect Flipkart
              </div>
            ) : null}
            <div
              id={FLIPKART_ADS_LOGIN_WRAPPER_ID}
              className={
                loginReady
                  ? 'absolute inset-0 z-10 overflow-hidden opacity-[0.02] [&>*]:h-full [&>*]:w-full [&_button]:h-full [&_button]:w-full [&_a]:h-full [&_a]:w-full'
                  : 'min-h-9 min-w-[10rem]'
              }
              data-testid="fk-ads-login-wrapper"
              hidden={!loginReady}
            />
          </div>
          {reconnectCredentialId ? (
            <p className="text-xs text-foreground-muted">Reconnecting — use Connect Flipkart</p>
          ) : null}
        </div>
      </div>

      <div className="bg-white px-4 pb-4 pt-1 sm:px-5 sm:pb-5">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : isError ? (
          <div className="rounded-xl border border-error/20 bg-error-subtle p-4 text-sm text-error-ink">
            Could not load connections.{' '}
            <button type="button" className="font-medium underline" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        ) : showEmpty ? (
          <div className="flex min-h-[240px] flex-col items-center justify-center rounded-xl border border-dashed border-[#d5dbe3] bg-white px-6 py-10 text-center">
            <FlipkartEmptyMark />
            <p className="mt-4 text-sm font-semibold text-brand-ink">
              No Flipkart accounts connected yet.
            </p>
            <p className="mt-1 max-w-md text-sm text-foreground-muted">
              Connect your Flipkart advertising account to get started with insights and automation.
            </p>
            <Button
              type="button"
              className="mt-4 h-10 rounded-lg bg-brand-ink px-4 text-white hover:bg-chrome-border"
              disabled={!loginReady}
              onClick={activateFlipkartAdsLogin}
            >
              <Link2 className="h-3.5 w-3.5" aria-hidden />
              Connect Flipkart
            </Button>
          </div>
        ) : (
          <FlipkartAdsAccountListing
            connections={connections}
            reconnectPending={bootstrapQuery.isFetching}
            onReconnect={(id) => {
              if (reconnectCredentialId === id) {
                void bootstrapQuery.refetch();
              } else {
                setReconnectCredentialId(id);
              }
              showToast('Click Connect Flipkart to finish re-authorization', 'success');
            }}
          />
        )}
      </div>
    </section>
  );
}

function activateFlipkartAdsLogin() {
  const root = document.getElementById(FLIPKART_ADS_LOGIN_WRAPPER_ID);
  const control = root?.querySelector<HTMLElement>('button, a, [role="button"]');
  control?.click();
}

function FlipkartEmptyMark() {
  return (
    <div className="relative h-16 w-20" aria-hidden>
      <div className="flex h-14 w-16 flex-col overflow-hidden rounded-lg border border-[#e4e7ec] bg-white shadow-sm">
        <div className="flex h-3.5 items-center gap-1 border-b border-[#eef0f3] bg-[#f7f8fa] px-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-[#d5dbe3]" />
          <span className="h-1.5 w-1.5 rounded-full bg-[#d5dbe3]" />
          <span className="h-1.5 w-1.5 rounded-full bg-[#d5dbe3]" />
        </div>
        <div className="flex flex-1 items-center justify-center">
          <img src="/flipkart.svg" alt="" className="h-6 w-6 object-contain" />
        </div>
      </div>
      <span className="absolute -bottom-0.5 -right-0.5 flex h-6 w-6 items-center justify-center rounded-full border border-[#e4e7ec] bg-white shadow-sm">
        <Link2 className="h-3 w-3 text-brand-deep" />
      </span>
      <span className="absolute -right-1 top-1 text-[10px] leading-none text-amber-400">✦</span>
    </div>
  );
}

export function FlipkartAdsAccountsTable({ accounts }: { accounts: FlipkartAdsAdAccount[] }) {
  if (accounts.length === 0) {
    return <p className="text-xs text-foreground-muted">No ad accounts synced yet.</p>;
  }

  return (
    <TableWrap compact className="rounded-lg border border-border">
      <Table compact>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Business account</TableHead>
            <TableHead>Ad account</TableHead>
          </TableRow>
        </TableHeader>
        <tbody>
          {accounts.map((account) => (
            <TableRow key={account.id}>
              <TableCell compact className="font-medium">
                {account.name || 'Untitled account'}
              </TableCell>
              <TableCell compact>{account.status || '—'}</TableCell>
              <TableCell compact className="font-mono text-foreground-muted">
                {account.business_account_id}
              </TableCell>
              <TableCell compact className="font-mono text-foreground-muted">
                {account.ad_account_id}
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </Table>
    </TableWrap>
  );
}

export function FlipkartAdsCampaignsTable({ campaigns }: { campaigns: FlipkartAdsCampaign[] }) {
  if (campaigns.length === 0) {
    return (
      <p className="text-xs text-foreground-muted">No campaigns synced yet.</p>
    );
  }

  return (
    <TableWrap compact className="rounded-lg border border-border">
      <Table compact>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Budget</TableHead>
            <TableHead>ID</TableHead>
          </TableRow>
        </TableHeader>
        <tbody>
          {campaigns.map((campaign) => (
            <TableRow key={campaign.id}>
              <TableCell compact className="font-medium">
                {campaign.name || 'Untitled campaign'}
              </TableCell>
              <TableCell compact>{campaign.status || '—'}</TableCell>
              <TableCell compact>{campaign.campaign_type || '—'}</TableCell>
              <TableCell compact>
                {campaign.daily_budget != null ? campaign.daily_budget.toLocaleString() : '—'}
              </TableCell>
              <TableCell compact className="font-mono text-foreground-muted">
                {campaign.campaign_id}
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </Table>
    </TableWrap>
  );
}

export function FlipkartAdsConnectionResources({ credentialId }: { credentialId: string }) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const accountsQuery = useQuery({
    queryKey: flipkartAdsAccountsQueryKey(credentialId),
    queryFn: async () => {
      const res = await flipkartAdsApi.listAccounts(credentialId);
      if (!res.success) throw new Error('Failed to load Flipkart Ads accounts');
      return res.data.accounts;
    },
  });
  const accounts = accountsQuery.data ?? [];

  const syncAccounts = useMutation({
    mutationFn: async () => {
      const res = await flipkartAdsApi.syncAccounts(credentialId);
      if (!res.success) throw new Error('Could not sync Flipkart Ads accounts');
      return res.data;
    },
    onSuccess: (result) => {
      showToast(
        `Synced ${result.upserted} Flipkart ad account${result.upserted === 1 ? '' : 's'}`,
        'success'
      );
      void queryClient.invalidateQueries({ queryKey: FLIPKART_ADS_ALL_ACCOUNTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FLIPKART_ADS_OAUTH_CONNECTIONS_QUERY_KEY });
    },
    onError: (err: unknown) => {
      showToast(axiosErrorMessage(err) || 'Flipkart Ads account sync failed', 'error');
    },
  });

  return (
    <div className="mt-3 space-y-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-medium text-foreground-muted">Ad accounts</p>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={syncAccounts.isPending}
            onClick={() => syncAccounts.mutate()}
          >
            {syncAccounts.isPending ? (
              <Spinner className="h-3.5 w-3.5 mr-1.5" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden />
            )}
            Sync accounts
          </Button>
        </div>
        {accountsQuery.isLoading ? (
          <div className="flex items-center gap-2 text-xs text-foreground-muted">
            <Spinner className="h-3.5 w-3.5" />
            Loading accounts…
          </div>
        ) : accountsQuery.isError ? (
          <p className="text-xs text-error-ink">Could not load accounts.</p>
        ) : (
          <FlipkartAdsAccountsTable accounts={accounts} />
        )}
      </div>
    </div>
  );
}

export function FlipkartAdsConnectionCampaigns({
  credentialId,
  hasAccounts = false,
  accountsLoading = false,
}: {
  credentialId: string;
  hasAccounts?: boolean;
  accountsLoading?: boolean;
}) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const campaignsQuery = useQuery({
    queryKey: flipkartAdsCampaignsQueryKey(credentialId),
    queryFn: async () => {
      const res = await flipkartAdsApi.listCampaigns(credentialId);
      if (!res.success) throw new Error('Failed to load Flipkart Ads campaigns');
      return res.data.campaigns;
    },
  });

  const sync = useMutation({
    mutationFn: async () => {
      const res = await flipkartAdsApi.syncCampaigns(credentialId);
      if (!res.success) throw new Error('Could not sync Flipkart Ads campaigns');
      return res.data;
    },
    onSuccess: (result) => {
      showToast(
        `Synced ${result.upserted} Flipkart campaign${result.upserted === 1 ? '' : 's'}`,
        'success'
      );
      void queryClient.invalidateQueries({ queryKey: flipkartAdsCampaignsQueryKey(credentialId) });
      void queryClient.invalidateQueries({ queryKey: FLIPKART_ADS_OAUTH_CONNECTIONS_QUERY_KEY });
    },
    onError: (err: unknown) => {
      showToast(axiosErrorMessage(err) || 'Flipkart Ads campaign sync failed', 'error');
    },
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-foreground-muted">Campaigns</p>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={sync.isPending || !hasAccounts || accountsLoading}
          title={hasAccounts ? undefined : 'Sync ad accounts first'}
          onClick={() => sync.mutate()}
        >
          {sync.isPending ? (
            <Spinner className="h-3.5 w-3.5 mr-1.5" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden />
          )}
          Sync campaigns
        </Button>
      </div>
      {!hasAccounts && !accountsLoading ? (
        <p className="text-xs text-foreground-muted">
          Sync ad accounts first. Campaign list requires an ad account id.
        </p>
      ) : null}
      {campaignsQuery.isLoading ? (
        <div className="flex items-center gap-2 text-xs text-foreground-muted">
          <Spinner className="h-3.5 w-3.5" />
          Loading campaigns…
        </div>
      ) : campaignsQuery.isError ? (
        <p className="text-xs text-error-ink">Could not load campaigns.</p>
      ) : (
        <FlipkartAdsCampaignsTable campaigns={campaignsQuery.data ?? []} />
      )}
    </div>
  );
}

function FlipkartRegionHeading({ code, label }: { code: string; label: string }) {
  return (
    <h3 className="flex items-center gap-2 text-sm font-medium text-brand-ink">
      <span className="inline-flex h-6 w-8 items-center justify-center rounded-md bg-[#f3f4f6] text-[11px] font-semibold tracking-wide text-foreground-muted">
        {code}
      </span>
      <span>{label}</span>
    </h3>
  );
}

export function FlipkartAdsAccountListing({
  connections,
  reconnectPending = false,
  onReconnect,
}: {
  connections: FlipkartAdsOauthConnection[];
  reconnectPending?: boolean;
  onReconnect?: (id: string) => void;
}) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const accountsQuery = useQuery({
    queryKey: FLIPKART_ADS_ALL_ACCOUNTS_QUERY_KEY,
    queryFn: async () => {
      const res = await flipkartAdsApi.listAllAccounts();
      if (!res.success) throw new Error('Failed to load Flipkart Ads accounts');
      return res.data.accounts;
    },
  });
  const accounts = accountsQuery.data ?? [];
  const connectionById = useMemo(
    () => new Map(connections.map((connection) => [connection.id, connection])),
    [connections]
  );
  const groups = useMemo(
    () =>
      ADVERTISING_ACCOUNT_REGIONS.map((region) => ({
        code: region.id,
        label: region.label,
        accounts: accounts.filter((account) => flipkartAccountRegion(account) === region.id),
      })).filter((group) => group.accounts.length > 0),
    [accounts]
  );

  const syncAccounts = useMutation({
    mutationFn: async () => {
      let upserted = 0;
      for (const connection of connections) {
        const res = await flipkartAdsApi.syncAccounts(connection.id);
        if (!res.success) throw new Error('Could not sync Flipkart Ads accounts');
        upserted += res.data.upserted;
      }
      return upserted;
    },
    onSuccess: (upserted) => {
      showToast(
        `Synced ${upserted} Flipkart ad account${upserted === 1 ? '' : 's'}`,
        'success'
      );
      void queryClient.invalidateQueries({ queryKey: FLIPKART_ADS_ALL_ACCOUNTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FLIPKART_ADS_OAUTH_CONNECTIONS_QUERY_KEY });
    },
    onError: (err: unknown) => {
      showToast(axiosErrorMessage(err) || 'Flipkart Ads account sync failed', 'error');
    },
  });

  const syncButton = (
    <Button
      type="button"
      size="sm"
      variant="secondary"
      disabled={syncAccounts.isPending || connections.length === 0}
      onClick={() => syncAccounts.mutate()}
    >
      {syncAccounts.isPending ? (
        <Spinner className="h-3.5 w-3.5 mr-1.5" />
      ) : (
        <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden />
      )}
      Sync accounts
    </Button>
  );

  if (accountsQuery.isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (accountsQuery.isError) {
    return (
      <div className="rounded-xl border border-error/20 bg-error-subtle p-4 text-sm text-error-ink">
        Could not load Flipkart ad accounts.{' '}
        <button type="button" className="font-medium underline" onClick={() => void accountsQuery.refetch()}>
          Retry
        </button>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-[#d5dbe3] px-4 py-6">
        <p className="text-sm text-foreground-muted">No Flipkart ad accounts synced yet.</p>
        {syncButton}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {groups.map((group, groupIndex) => (
        <section key={group.code}>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <FlipkartRegionHeading code={group.code} label={group.label} />
            {groupIndex === 0 ? syncButton : null}
          </div>
          <ul className="space-y-2">
            {group.accounts.map((account) => {
              const connection = connectionById.get(account.credential_id);
              const label = flipkartAdAccountLabel(account);
              const detail = ['Flipkart Ads', account.status, account.ad_account_id]
                .filter(Boolean)
                .join(' · ');
              return (
                <ConnectionAccountCard
                  key={account.id}
                  title={label}
                  actions={
                    connection && needsReconnect(connection.health) ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={reconnectPending}
                        onClick={() => onReconnect?.(connection.id)}
                      >
                        <Link2 className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                        Reconnect
                      </Button>
                    ) : undefined
                  }
                >
                  <p className="mt-0.5 truncate text-xs text-foreground-subtle">{detail}</p>
                  {account.business_account_id ? (
                    <p className="mt-0.5 truncate text-xs text-foreground-muted">
                      Business account: {account.business_account_id}
                    </p>
                  ) : null}
                </ConnectionAccountCard>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

export function FlipkartAdsConnectionsList({
  connections,
  reconnectPending = false,
  onReconnect,
  renderExtra,
}: {
  connections: FlipkartAdsOauthConnection[];
  reconnectPending?: boolean;
  onReconnect?: (id: string) => void;
  renderExtra?: (connection: FlipkartAdsOauthConnection) => ReactNode;
}) {
  if (connections.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-surface p-6 text-center text-foreground-muted text-sm">
        No Flipkart Ads advertisers connected yet.
      </div>
    );
  }

  return (
    <div>
      <h3 className="mb-2 flex items-center gap-2 px-1 text-sm font-medium text-brand-ink">
        <span className="inline-flex h-6 w-8 items-center justify-center rounded-md bg-[#f3f4f6] text-[11px] font-semibold tracking-wide text-foreground-muted">
          IN
        </span>
        <span>India</span>
      </h3>
    <ul className="space-y-2">
      {connections.map((c) => (
        <li
          key={c.id}
          className="rounded-xl border border-border bg-surface px-4 py-3"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium text-foreground truncate">
                {c.display_name || c.advertiser_id || 'Flipkart Ads connection'}
              </p>
              <p className="text-xs text-foreground-subtle mt-0.5">
                {healthLabel(c.health)}
                {c.days_until_expiry != null
                  ? ` · ${c.days_until_expiry} day${c.days_until_expiry === 1 ? '' : 's'} left`
                  : ''}
                {c.created_by_email ? ` · ${c.created_by_email}` : ''}
              </p>
            </div>
            {needsReconnect(c.health) ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={reconnectPending}
                onClick={() => onReconnect?.(c.id)}
              >
                <Link2 className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                Reconnect
              </Button>
            ) : null}
          </div>
          {renderExtra?.(c)}
        </li>
      ))}
    </ul>
    </div>
  );
}
